import React from 'react';
import { Github, Globe, Plus, Edit2, Trash2 } from 'lucide-react';
import Cropper from 'react-easy-crop';

export default function ProjectsSection({
    t,
    portfolio,
    currentLang,
    getText,
    isAdmin,
    showAddProject,
    setShowAddProject,
    newProject,
    setNewProject,
    addProject,
    handleEditProject,
    deleteProject,
    editingProjectId,
    setEditingProjectId,
    // Cropper props
    crop,
    setCrop,
    zoom,
    setZoom,
    onCropComplete,
    projectImageFile,
    setProjectImageFile,
    handleFileSelect,
    uploadingProject,
    techIcons,
    processCroppedImage // New prop
}) {
    return (
        <section id={t.nav[3].toLowerCase().replace(/\s+/g, '-')} className="py-24 px-4 bg-slate-900">
            <div className="max-w-6xl mx-auto">
                <div className="relative mb-16">
                    <h2 className="text-4xl md:text-5xl font-bold text-center">
                        <span className="bg-clip-text text-transparent bg-gradient-to-r from-cyan-400 to-blue-500">
                            {t.projects}
                        </span>
                    </h2>
                    {isAdmin && (
                        <button onClick={() => {
                            setNewProject({
                                title: { es: '', pt: '', en: '' },
                                description: { es: '', pt: '', en: '' },
                                tags: '',
                                image_url: '',
                                github_url: '',
                                demo_url: ''
                            });
                            setEditingProjectId(null);
                            setProjectImageFile(null); // Reset image state
                            setShowAddProject(true);
                        }} className="absolute top-1/2 -translate-y-1/2 right-0 bg-cyan-500/20 text-cyan-400 p-2 rounded-lg hover:bg-cyan-500 hover:text-white transition-all">
                            <Plus size={24} />
                        </button>
                    )}
                </div>

                {/* Add/Edit Project Modal */}
                {showAddProject && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/80 backdrop-blur-sm animate-fade-in">
                        <div className="bg-slate-800 p-8 rounded-2xl w-full max-w-4xl border border-slate-700 max-h-[90vh] overflow-y-auto shadow-2xl">
                            <h3 className="text-2xl font-bold mb-6 text-white border-b border-slate-700 pb-4">{editingProjectId ? 'Editar Proyecto' : t.addProject}</h3>

                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                                <div className="space-y-4">
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                                        <input type="text" placeholder="Título (ES)" value={newProject.title.es} onChange={(e) => setNewProject({ ...newProject, title: { ...newProject.title, es: e.target.value } })} className="bg-slate-900 border border-slate-700 rounded-lg p-3 w-full" />
                                        <input type="text" placeholder="Título (PT)" value={newProject.title.pt} onChange={(e) => setNewProject({ ...newProject, title: { ...newProject.title, pt: e.target.value } })} className="bg-slate-900 border border-slate-700 rounded-lg p-3 w-full" />
                                        <input type="text" placeholder="Título (EN)" value={newProject.title.en} onChange={(e) => setNewProject({ ...newProject, title: { ...newProject.title, en: e.target.value } })} className="bg-slate-900 border border-slate-700 rounded-lg p-3 w-full" />
                                    </div>

                                    <textarea placeholder="Descripción (ES)" value={newProject.description.es} onChange={(e) => setNewProject({ ...newProject, description: { ...newProject.description, es: e.target.value } })} className="bg-slate-900 border border-slate-700 rounded-lg p-3 w-full h-24" />
                                    <textarea placeholder="Descripción (PT)" value={newProject.description.pt} onChange={(e) => setNewProject({ ...newProject, description: { ...newProject.description, pt: e.target.value } })} className="bg-slate-900 border border-slate-700 rounded-lg p-3 w-full h-24" />
                                    <textarea placeholder="Descripción (EN)" value={newProject.description.en} onChange={(e) => setNewProject({ ...newProject, description: { ...newProject.description, en: e.target.value } })} className="bg-slate-900 border border-slate-700 rounded-lg p-3 w-full h-24" />

                                    <div className="space-y-2">
                                        <label className="text-sm font-medium text-slate-400">Tecnologías (separadas por coma)</label>
                                        <input type="text" placeholder="React, Node.js, Supabase..." value={newProject.tags} onChange={(e) => setNewProject({ ...newProject, tags: e.target.value })} className="bg-slate-900 border border-slate-700 rounded-lg p-3 w-full" />
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <input type="text" placeholder="GitHub URL" value={newProject.github_url} onChange={(e) => setNewProject({ ...newProject, github_url: e.target.value })} className="bg-slate-900 border border-slate-700 rounded-lg p-3 w-full" />
                                        <input type="text" placeholder="Demo URL" value={newProject.demo_url} onChange={(e) => setNewProject({ ...newProject, demo_url: e.target.value })} className="bg-slate-900 border border-slate-700 rounded-lg p-3 w-full" />
                                    </div>
                                </div>

                                {/* Image Upload & Crop */}
                                <div className="space-y-4">
                                    <label className="block text-sm font-medium text-slate-400">Imagen del Proyecto</label>

                                    {!projectImageFile && !newProject.image_url && (
                                        <div className="border-2 border-dashed border-slate-700 rounded-xl h-64 flex flex-col items-center justify-center text-slate-500 hover:border-cyan-500/50 hover:bg-slate-900/50 transition-all">
                                            <input type="file" accept="image/*" onChange={(e) => handleFileSelect(e, 'project')} className="hidden" id="project-img-upload" />
                                            <label htmlFor="project-img-upload" className="cursor-pointer flex flex-col items-center">
                                                <Plus size={48} className="mb-2 opacity-50" />
                                                <span>Click para subir imagen</span>
                                                <span className="text-xs mt-2 opacity-70">JPG, PNG, GIF (Máx. 5MB)</span>
                                            </label>
                                        </div>
                                    )}

                                    {/* Preview Existing Image or Newly Uploaded Image */}
                                    {!projectImageFile && newProject.image_url && (
                                        <div className="relative h-64 rounded-xl overflow-hidden border border-slate-700 group">
                                            <img src={newProject.image_url} alt="Project" className="w-full h-full object-cover" />
                                            <button onClick={() => setNewProject({ ...newProject, image_url: '' })} className="absolute top-2 right-2 bg-red-600/80 p-2 rounded-full text-white opacity-0 group-hover:opacity-100 transition-opacity">
                                                <Trash2 size={16} />
                                            </button>
                                        </div>
                                    )}

                                    {/* Cropper UI */}
                                    {projectImageFile && (
                                        <div className="space-y-4">
                                            <div className="relative h-64 bg-slate-900 rounded-lg overflow-hidden border border-slate-700 select-none">
                                                <Cropper
                                                    image={URL.createObjectURL(projectImageFile)}
                                                    crop={crop}
                                                    zoom={zoom}
                                                    aspect={16 / 9}
                                                    onCropChange={setCrop}
                                                    onCropComplete={onCropComplete}
                                                    onZoomChange={setZoom}
                                                />
                                            </div>
                                            <div className="flex items-center gap-4">
                                                <span className="text-sm text-slate-400">Zoom</span>
                                                <input
                                                    type="range"
                                                    value={zoom}
                                                    min={1}
                                                    max={3}
                                                    step={0.1}
                                                    aria-labelledby="Zoom"
                                                    onChange={(e) => setZoom(e.target.value)}
                                                    className="w-full accent-cyan-500"
                                                />
                                            </div>
                                            <div className="flex gap-2">
                                                <button
                                                    onClick={processCroppedImage}
                                                    className="flex-1 bg-green-600 hover:bg-green-500 text-white py-2 rounded-lg transition-colors font-medium shadow-lg"
                                                >
                                                    Confirmar y Subir Imagen
                                                </button>
                                                <button onClick={() => setProjectImageFile(null)} className="px-4 py-2 border border-red-500/50 text-red-400 hover:bg-red-500/10 rounded-lg transition-colors">
                                                    Cancelar
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="flex gap-4 mt-8 pt-6 border-t border-slate-700">
                                <button
                                    onClick={addProject}
                                    className="flex-1 bg-cyan-600 hover:bg-cyan-500 text-white py-3 rounded-xl transition-all font-bold text-lg shadow-lg hover:shadow-cyan-500/20"
                                >
                                    {editingProjectId ? 'Actualizar Proyecto' : t.save}
                                </button>
                                <button
                                    onClick={() => {
                                        setShowAddProject(false);
                                        setEditingProjectId(null);
                                        setProjectImageFile(null);
                                    }}
                                    className="flex-1 bg-slate-700 hover:bg-slate-600 px-4 rounded-xl transition-colors font-medium"
                                >
                                    {t.cancel}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Projects Grid */}
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {portfolio.projects.map(project => (
                        <div key={project.id} className="group bg-slate-800/30 rounded-2xl overflow-hidden border border-slate-700/50 hover:border-cyan-500/50 transition-all duration-500 hover:-translate-y-2 hover:shadow-2xl hover:shadow-cyan-500/10 flex flex-col h-full animate-on-scroll opacity-0 translate-y-10">
                            <div className="relative overflow-hidden h-48 sm:h-52">
                                <div className="absolute inset-0 bg-gradient-to-t from-slate-900 to-transparent opacity-60 z-10 transition-opacity group-hover:opacity-40"></div>
                                {project.image_url ? (
                                    <img src={project.image_url} alt={project.title} className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700" />
                                ) : (
                                    <div className="w-full h-full bg-slate-800 flex items-center justify-center">
                                        <Globe size={48} className="text-slate-600" />
                                    </div>
                                )}

                                {/* Admin Actions */}
                                {isAdmin && (
                                    <div className="absolute top-2 right-2 z-20 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                        <button onClick={() => handleEditProject(project)} className="bg-slate-900/80 p-2 rounded-full text-cyan-400 hover:text-white hover:bg-cyan-600 transition-colors backdrop-blur-sm" aria-label="Edit Project">
                                            <Edit2 size={16} />
                                        </button>
                                        <button onClick={() => deleteProject(project.id)} className="bg-slate-900/80 p-2 rounded-full text-red-400 hover:text-white hover:bg-red-600 transition-colors backdrop-blur-sm" aria-label="Delete Project">
                                            <Trash2 size={16} />
                                        </button>
                                    </div>
                                )}

                                {/* Links Overlay */}
                                <div className="absolute bottom-0 left-0 w-full p-4 z-20 flex justify-end gap-3 translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                                    {project.github_url && (
                                        <a href={project.github_url} target="_blank" rel="noopener noreferrer" className="bg-slate-900/90 p-2 rounded-lg text-white hover:text-cyan-400 hover:bg-slate-800 transition-colors" title="Ver Código" aria-label="Ver Código Fuente">
                                            <Github size={20} />
                                        </a>
                                    )}
                                    {project.demo_url && (
                                        <a href={project.demo_url} target="_blank" rel="noopener noreferrer" className="bg-cyan-600/90 p-2 rounded-lg text-white hover:bg-cyan-500 transition-colors shadow-lg" title="Ver Demo" aria-label="Ver Demo en Vivo">
                                            <Globe size={20} />
                                        </a>
                                    )}
                                </div>
                            </div>

                            <div className="p-6 flex-1 flex flex-col">
                                <h3 className="text-2xl font-bold mb-3 bg-clip-text text-transparent bg-gradient-to-r from-white to-slate-400 group-hover:from-cyan-400 group-hover:to-blue-400 transition-all duration-300">
                                    {getText(project.title, currentLang)}
                                </h3>
                                <p className="text-slate-400 mb-6 line-clamp-3 leading-relaxed flex-1">
                                    {getText(project.description, currentLang)}
                                </p>
                                <div className="flex flex-wrap gap-2 mt-auto">
                                    {project.tags && project.tags.split(',').map((tag, i) => {
                                        const trimmedTag = tag.trim();
                                        const iconClass = techIcons ? techIcons[trimmedTag] : null;
                                        return (
                                            <span key={i} className="text-xs px-3 py-1 rounded-full bg-slate-700/50 text-cyan-400 border border-slate-600/50 flex items-center gap-1.5">
                                                {iconClass && <i className={iconClass}></i>}
                                                {trimmedTag}
                                            </span>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}
